import React, { useState, useEffect } from 'react';
import { Search, Sparkles, Loader2, TrendingUp, Menu, X, ShieldCheck, Zap, FileText, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PricingModal from '../components/PricingModal';
import FairUsePolicyModal from '../components/FairUsePolicyModal';
import AuthModal from '../components/AuthModal';
import UsageBadge from '../components/UsageBadge';
import UserMenu from '../components/UserMenu';
import GuideModal from '../components/GuideModal';
import StatsBar from '../components/StatsBar';
import apiService from '../services/api';
import { isValidUrl } from '../utils/helpers';
import VersionFooter from '../components/VersionFooter';
// At top with other imports
import SubscriptionPlans from '../components/SubscriptionPlans';
import BillingManagement from '../components/BillingManagement';
import useCanonical from '../hooks/useCanonical';

const LandingPage = ({ user, onUserUpdate, onLogout }) => {
  useCanonical('https://aeo.thatworkx.com/');
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // Changed from '' to null to store object
  const [usage, setUsage] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pricingModalTab, setPricingModalTab] = useState('subscription');
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [guideModalTab, setGuideModalTab] = useState('howto');
  const [showFairUseModal, setShowFairUseModal] = useState(false);
  const [pendingAnalysis, setPendingAnalysis] = useState(null); // Store URL/keywords for post-OTP analysis
  // At top of component
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false);
  const [showBillingManagement, setShowBillingManagement] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') === 'success') {
      navigate('/dashboard?subscription=success');
    } else if (params.get('subscription') === 'cancelled') {
      navigate('/dashboard?subscription=cancelled');
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchUsage();
    }
  }, [user]);

  const fetchUsage = async () => {
    try {
      const sessionData = await apiService.auth.getSession();
      if (sessionData.authenticated && sessionData.usage) {
        setUsage(sessionData.usage);
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  // Handle successful OTP - run pending analysis if exists
  const handleAuthSuccess = async (userData) => {
    // CRITICAL FIX: Fetch fresh session data to get subscription details
    // OTP response may have minimal data, session endpoint has complete data
    try {
      const sessionData = await apiService.auth.getSession();
      if (sessionData.authenticated && sessionData.user) {
        // Use session user data (has subscription) instead of OTP user data
        onUserUpdate(sessionData.user);
        userData = sessionData.user; // Use for analysis if needed
      } else {
        // Fallback to OTP user data if session fetch fails
        onUserUpdate(userData);
      }
    } catch (error) {
      console.error('Session fetch after OTP failed:', error);
      // Still use OTP user data as fallback
      onUserUpdate(userData);
    }
    
    // If there's a pending analysis, run it immediately
    if (pendingAnalysis) {
      setLoading(true);
      
      // Declare outside try block so it's accessible in catch
      const keywordsArray = pendingAnalysis.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);
      
      try {
        const result = await apiService.analysis.runAnalysis(
          pendingAnalysis.url,
          keywordsArray,
          userData.email
        );

        // Clear pending analysis
        setPendingAnalysis(null);
        
        // Navigate to dashboard with results
        navigate('/dashboard', { state: { result } });
      } catch (err) {
        // Navigate to Dashboard even on error
        const errorResult = {
          results: {
            url: pendingAnalysis.url,
            targetKeywords: keywordsArray,
            overallScore: 0,
            overallGrade: 'F',
            analyzers: {
              technicalFoundation: null,
              contentStructure: null,
              pageLevelEEAT: null,
              queryMatch: null,
              aiVisibility: null,
              siteLevelEEAT: null
            },
            recommendations: [],
            processingTime: 0,
            weights: {
              technicalFoundation: 0.30,
              contentStructure: 0.30,
              pageLevelEEAT: 0.20,
              queryMatch: 0.10,
              aiVisibility: 0.10
            }
            },
          usage: null, // Will be fetched on Dashboard mount
          error: {
            message: err.message || 'Analysis failed',
            blockDetection: err.blockDetection || null,
            aeoImpact: err.aeoImpact || null,
            recommendation: err.recommendation || null
          }
        };
        
        setPendingAnalysis(null);
        navigate('/dashboard', { state: { result: errorResult } });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await apiService.auth.logout();
      onLogout(); // Clear user in App.js
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setError('');

    // Validate URL
    if (!isValidUrl(url)) {
      setError({ message: 'Please enter a valid URL' });
      return;
    }

    // Check if user is authenticated
    if (!user) {
      // Store the analysis details to run after OTP
      setPendingAnalysis({ url, keywords });
      setShowAuthModal(true);
      return;
    }

    // Run analysis
    setLoading(true);
    
    // Declare outside try block so it's accessible in catch
    const keywordsArray = keywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    
    try {
      const result = await apiService.analysis.runAnalysis(
        url,
        keywordsArray,
        user.email
      );

      // Navigate to dashboard with results
      navigate('/dashboard', { state: { result } });
    } catch (err) {
      // CRITICAL CHANGE: Navigate to Dashboard even on error
      // Pass error state so Dashboard can display it prominently
      const errorResult = {
        results: {
          url,
          targetKeywords: keywordsArray,
          overallScore: 0,
          overallGrade: 'F',
            analyzers: {
              technicalFoundation: null,
              contentStructure: null,
              pageLevelEEAT: null,
              queryMatch: null,
              aiVisibility: null,
              robotsTxt: null,
              markdownFiles: null,
              siteLevelEEAT: null
            },
            recommendations: [],
            processingTime: 0,
            weights: {
              technicalFoundation: 0.20,
              contentStructure: 0.20,
              pageLevelEEAT: 0.15,
              queryMatch: 0.15,
              aiVisibility: 0.10,
              robotsTxt: 0.10,
              markdownFiles: 0.10
            }
          },
        usage: usage || { current: 0, limit: 5, remaining: 5 },
        error: {
          message: err.message || 'Analysis failed',
          blockDetection: err.blockDetection || null,
          aeoImpact: err.aeoImpact || null,
          recommendation: err.recommendation || null
        }
      };
      
      // Navigate to Dashboard with error state
      navigate('/dashboard', { state: { result: errorResult } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {/* Stats Bar */}
    <StatsBar />
    <div className="min-h-screen bg-gradient-to-b from-dark-950 via-dark-900 to-dark-950">
      {/* Enhanced Header */}
      <header className="border-b border-dark-800 sticky top-0 bg-dark-950/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
          {/* Logo */}
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-primary-500" />
              <img 
                src="/AEO-thatworkx-logo.svg" 
                alt="AEO Suite by Thatworkx" 
                className="h-16"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <button
                onClick={() => {
                  setPricingModalTab('services');
                  setShowPricingModal(true);
                }}
                className="text-dark-400 hover:text-white transition-colors text-sm"
              >
                Our Services
              </button>
              <a href="#ecosystem" className="text-dark-400 hover:text-white transition-colors text-sm font-semibold">
                Ecosystem
              </a>
              <a href="#faq" className="text-dark-400 hover:text-white transition-colors text-sm">
                What is AEO?
              </a>
              <button
                onClick={() => {
                  setGuideModalTab('howto');
                  setShowGuideModal(true);
                }}
                className="text-dark-200 hover:text-white transition-colors"
              >
                Documentation 
              </button>
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <UserMenu 
                    user={user} 
                    onLogout={handleLogout}
                    onManageSubscription={() => setShowBillingManagement(true)}
                    onUpgrade={() => setShowSubscriptionPlans(true)}
                  />
                  {/* Only show Upgrade button for Free users */}
                  {user.subscription?.type === 'free' || !user.subscription?.type ? (
                    <button
                      onClick={() => {
                        setShowSubscriptionPlans(true);
                      }}
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors text-sm"
                    >
                      Upgrade
                    </button>
                  ) : null}
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="hidden md:block px-4 py-2 text-dark-400 hover:text-white transition-colors text-sm"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors text-sm"
                  >
                    Get Started
                  </button>
                </>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-dark-400 hover:text-white transition-colors"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-dark-800 pt-4">
              <nav className="flex flex-col gap-4">
                <button
                  onClick={() => {
                    setPricingModalTab('services');
                    setShowPricingModal(true);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-dark-400 hover:text-white transition-colors"
                >
                  Our Services
                </button>
                <a href="#ecosystem" className="text-dark-400 hover:text-white transition-colors font-semibold">
                  Ecosystem
                </a>
                <a href="#faq" className="text-dark-400 hover:text-white transition-colors">
                  What is AEO?
                </a>
                <button
                  onClick={() => {
                    setGuideModalTab('howto');
                    setShowGuideModal(true);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-dark-200 hover:text-white hover:bg-dark-800 transition-colors"
                >
                  Documentation 
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500/10 border border-primary-500/30 rounded-full text-primary-500 text-sm font-medium mb-6">
            <TrendingUp size={16} />
            Answer Engine Optimization
          </div>
          
          <h2 className="text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
            Be the Source <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-blue-500">
              AI Trusts.
            </span>
            <span className="text-white"> Own the Spotlight.</span>
          </h2>
          
          <p className="text-xl text-dark-400 max-w-2xl mx-auto mb-12 leading-relaxed">
            Build your <strong>Golden Record</strong> on your website. Amplify your voice on social. Get cited by the world's most powerful AI engines.
          </p>

          {/* Analysis Form */}
          <form onSubmit={handleAnalyze} className="space-y-4">
            <div className="relative">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter your Website URL (e.g., https://yourbrand.com)"
                className="w-full px-6 py-4 pr-12 bg-dark-800 border border-dark-700 rounded-xl text-white placeholder-dark-500 focus:outline-none focus:border-primary-500 transition-colors text-lg"
                disabled={loading}
              />
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-500" size={24} />
            </div>

            <div className="relative">
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="Target keywords for your Spotlight (comma-separated, optional)"
                className="w-full px-6 py-3 bg-dark-800 border border-dark-700 rounded-xl text-white placeholder-dark-500 focus:outline-none focus:border-primary-500 transition-colors"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="space-y-3">
                {/* Main error message */}
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
                  {typeof error === 'string' ? error : error.message}
                </div>

                {/* Bot blocking details (if available) */}
                {typeof error === 'object' && error.blockDetection && error.blockDetection.isBlocked && (
                  <div className="p-6 bg-orange-500/10 border border-orange-500/30 rounded-lg space-y-4">
                    {/* Block Type */}
                    <div className="flex items-start gap-3">
                      <div className="text-orange-500 font-semibold text-sm min-w-[120px]">
                        🤖 Blocking Type:
                      </div>
                      <div className="text-orange-300 text-sm font-semibold">
                        {error.blockDetection.blockType}
                      </div>
                    </div>

                    {/* AEO Impact */}
                    {error.aeoImpact && (
                      <div className="flex items-start gap-3">
                        <div className="text-orange-500 font-semibold text-sm min-w-[120px]">
                          ⚠️ AEO Impact:
                        </div>
                        <div className="text-orange-300 text-sm">
                          {error.aeoImpact}
                        </div>
                      </div>
                    )}

                    {/* Blocked Bots List (if Cloudflare) */}
                    {error.blockDetection.blockedBots && error.blockDetection.blockedBots.length > 0 && (
                      <div className="bg-orange-900/20 rounded-lg p-4 space-y-2">
                        <div className="text-orange-400 font-semibold text-sm mb-3">
                          🚫 AI Bots Being Blocked:
                        </div>
                        <div className="space-y-2">
                          {error.blockDetection.blockedBots.map((bot, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-xs">
                              <span className="text-orange-500 font-mono bg-orange-900/30 px-2 py-1 rounded">
                                {bot.userAgent}
                              </span>
                              <span className="text-orange-300/80">
                                → {bot.impact}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendation - Expandable for long instructions */}
                    {error.recommendation && (
                      <div className="border-t border-orange-500/20 pt-4">
                        <div className="text-orange-500 font-semibold text-sm mb-2">
                          💡 How to Fix:
                        </div>
                        <div className="bg-dark-900 rounded p-4 text-orange-200/90 text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                          {error.recommendation}
                        </div>
                      </div>
                    )}

                    {/* Evidence */}
                    {error.blockDetection.evidence && error.blockDetection.evidence.length > 0 && (
                      <div className="border-t border-orange-500/20 pt-3">
                        <div className="text-orange-500 font-semibold text-xs mb-2">
                          🔍 Evidence:
                        </div>
                        <ul className="list-disc list-inside text-orange-300/80 text-xs space-y-1">
                          {error.blockDetection.evidence.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Quick Action Button */}
                    <div className="border-t border-orange-500/20 pt-4">
                      
                      <a href="https://dash.cloudflare.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg transition-colors"
                      >
                        Open Cloudflare Dashboard →
                      </a>
                    </div>
                    </div>
                  )}
                  </div>
                )}
            
            <button
              type="submit"
              disabled={loading || !url}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 text-white font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg shadow-lg shadow-primary-500/20"
            >
              {loading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Validating Readiness...
                </>
              ) : (
                <>
                  <Sparkles className="w-6 h-6" />
                  Validate my Website's AI Readiness
                </>
              )}
            </button>
          </form>
            
          {!user && (
            <p className="mt-6 text-sm text-dark-500">
              Free readiness analysis • No credit card required • ~5 second verification
            </p>
          )}
        </div>

        {/* The Ecosystem Section */}
        <section id="ecosystem" className="py-20 border-t border-dark-800 mt-20 scroll-mt-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Two Tools. One Unified Strategy.
            </h2>
            <p className="text-dark-400 max-w-2xl mx-auto">
              Answer Engine citations rely on "Triangulation." We ensure your social spotlight points directly to your authoritative source.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* The Source Loop - AI Optimize */}
            <div className="p-8 bg-dark-800/40 border border-dark-700 rounded-2xl hover:border-blue-500/30 transition-all group flex flex-col">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                  <ShieldCheck className="w-6 h-6 text-blue-500" />
                </div>
                <span className="text-xl font-bold text-blue-400">AI Optimize</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Build the Source</h3>
              <p className="text-dark-400 mb-6 leading-relaxed flex-1">
                <strong>AI Optimize</strong> is an online tool designed to validate your online presence for Answer Engine Optimization. We audit your site to ensure that when AI bots follow your signal, they find a perfectly structured <strong>Golden Record</strong> of your expertise.
              </p>
              <div className="flex flex-col gap-4">
                <button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="w-fit text-blue-400 hover:text-blue-300 font-semibold flex items-center gap-2 transition-colors underline decoration-blue-500/30 underline-offset-4"
                >
                  Verify my Source →
                </button>
                <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold">
                  <Sparkles size={16} />
                  Available now for Business Owners, Marketers and Web teams
                </div>
                <a 
                  href="/aeo-report-detailed-1779441554134.pdf" 
                  target="_blank"
                  className="w-fit text-xs text-blue-500/80 hover:text-blue-400 transition-colors flex items-center gap-1"
                >
                  <FileText size={12} />
                  View Sample Detailed Report
                </a>
              </div>
            </div>

            {/* The Spotlight Loop - AI Socialize */}
            <div className="p-8 bg-dark-800/40 border border-dark-700 rounded-2xl hover:border-primary-500/30 transition-all group flex flex-col">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
                  <TrendingUp className="w-6 h-6 text-primary-500" />
                </div>
                <span className="text-xl font-bold text-primary-400">AI Socialize</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Own the Spotlight</h3>
              <p className="text-dark-400 mb-6 leading-relaxed flex-1">
                <strong>AI Socialize</strong> is a browser extension that works directly with your social media sites to help you create and publish verified content. Stay in your creative flow while we ensure your social voice is entity-rich and ready for AI citation.
              </p>
              <div className="flex items-center gap-2 text-primary-400 text-sm font-semibold">
                <Zap size={16} />
                Coming Soon for Influencers, Content Creators and Brand Managers
              </div>
            </div>
          </div>
        </section>

        {/* Visibility Pillars Grid */}
        <div id="features" className="text-center mb-12 mt-20 scroll-mt-24">
          <h2 className="text-3xl font-bold text-white mb-4">
            The 4 Pillars of Visibility
          </h2>
          <p className="text-dark-400 max-w-2xl mx-auto">
            We audit your website across the four critical metrics that Answer Engines use to determine citation authority.
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            {
              title: 'Discovery',
              description: 'Can AI bots find your Source?',
              icon: Search,
            },
            {
              title: 'Clarity',
              description: 'Can AI bots understand you?',
              icon: Sparkles,
            },
            {
              title: 'Trust',
              description: 'Do AI bots believe you?',
              icon: ShieldCheck,
            },
            {
              title: 'Relevance',
              description: 'Are you answering the right questions?',
              icon: TrendingUp,
            },
          ].map((pillar, idx) => (
            <div
              key={idx}
              className="p-6 bg-dark-800/50 border border-dark-700 rounded-xl hover:border-dark-600 transition-colors text-center"
            >
              <div className="w-10 h-10 bg-dark-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                <pillar.icon size={20} className="text-primary-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                {pillar.title}
              </h3>
              <p className="text-xs text-dark-400">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        {/* Professional Reports Highlight - NEW SECTION */}
        <section id="reports" className="py-20 border-t border-dark-800 mt-20 scroll-mt-24">
          <div className="bg-gradient-to-br from-dark-800 to-dark-900 border border-dark-700 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-primary-500/10 transition-colors"></div>
            
            <div className="flex flex-col md:flex-row items-center gap-12 relative z-10">
              <div className="w-24 h-24 bg-primary-500/10 rounded-2xl flex items-center justify-center shrink-0">
                <FileText className="w-12 h-12 text-primary-500" />
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-500/20 border border-primary-500/30 rounded-full text-primary-400 text-xs font-bold mb-4 uppercase tracking-wider">
                  Pro Feature
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Professional AEO Audit Reports
                </h2>
                <p className="text-dark-400 text-lg leading-relaxed mb-6">
                  Get client-ready, professional PDF reports for every analysis. Our detailed reports break down your website performance across all 4 Visibility Pillars with strategic recommendations for AI optimization.
                </p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                  <a 
                    href="/aeo-report-detailed-1779441554134.pdf" 
                    target="_blank"
                    className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-primary-500/20 flex items-center gap-2"
                  >
                    <Download size={20} />
                    View Sample Detailed Report
                  </a>
                  <p className="text-sm text-dark-500">
                    * Available exclusively for Pro and Enterprise users
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-dark-900/50 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Why AEO is the New SEO
            </h2>
            <p className="text-dark-400 max-w-2xl mx-auto">
              AI-powered search engines are transforming how people find information. Traditional ranking is no longer enough; you need to be the source.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                stat: '60%',
                title: 'Informational Shift',
                description: '60% of users now prefer AI direct-answers over clicking through search results. If you aren\'t cited, you don\'t exist.',
              },
              {
                stat: '5x',
                title: 'High-Intent Traffic',
                description: 'Citations in AI answers act as expert recommendations, resulting in 5x higher conversion rates than standard SEO clicks.',
              },
              {
                stat: '300%',
                title: 'Exponential Growth',
                description: 'AI-agent traffic is growing at 300% annually. Future-proof your digital presence by building your Golden Record today.',
              },
            ].map((benefit, idx) => (
              <div key={idx} className="bg-dark-800 border border-dark-700 rounded-xl p-8 text-center hover:border-primary-500/50 transition-colors">
                <div className="text-5xl font-bold text-primary-500 mb-4">
                  {benefit.stat}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-dark-400 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - Critical for AEO */}
      <section id="faq" className="py-20 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              AEO FAQ
            </h2>
            <p className="text-dark-400">
              Everything you need to know about the "Source & Spotlight" strategy.
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                q: 'What is Answer Engine Optimization (AEO)?',
                a: 'AEO is the practice of ensuring your content is machine-readable and authoritative so it can be cited by AI engines like ChatGPT, Claude, and Perplexity. Unlike SEO which focuses on links, AEO focuses on "The Golden Record"—structured facts that AI trusts.',
              },
              {
                q: 'How does the "Source & Spotlight" strategy work?',
                a: 'Your website is "The Source"—the Golden Record of your expertise. Your social media is "The Spotlight"—the signal that grabs attention. We ensure that when AI sees your Spotlight, it can verify your claims against your Source.',
              },
              {
                q: 'What is a "Golden Record"?',
                a: 'A Golden Record is a technically perfect version of your content (optimized via AI Optimize) that contains valid Schema, E-E-A-T signals, and machine-readable summaries. It is the definitive receipt that AI engines use for citation.',
              },
              {
                q: 'Do I need technical knowledge?',
                a: 'No. AI Optimize provides clear, outcome-focused recommendations. For influencers using AI Socialize, the experience is invisible—we handle the technical verification in the background as you post.',
              },
              {
                q: 'Which AI engines do you optimize for?',
                a: 'Our principles apply to all major Answer Engines including OpenAI Search, Perplexity AI, Google AI Overviews, Microsoft Copilot, and Anthropic Claude.',
              },
            ].map((faq, idx) => (
              <details
                key={idx}
                className="bg-dark-800 border border-dark-700 rounded-xl p-6 hover:border-dark-600 transition-colors group"
              >
                <summary className="text-lg font-semibold text-white cursor-pointer flex items-center justify-between">
                  {faq.q}
                  <span className="text-primary-500 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-4 text-dark-400 leading-relaxed">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => {
                setGuideModalTab('howto');
                setShowGuideModal(true);
              }}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
            >
              View Full Ecosystem Documentation
            </button>
          </div>

          {/* CTA after FAQ */}
          <div className="mt-12 text-center p-8 bg-gradient-to-r from-primary-500/10 to-blue-500/10 border border-primary-500/30 rounded-2xl">
            <h3 className="text-2xl font-bold text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-blue-500">
              Ready to Own the Spotlight?
            </h3>
            <p className="text-dark-400 mb-6 max-w-2xl mx-auto">
              Build your Source today with a free AEO verification.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
              >
                Validate my Website
              </button>
              <button
                  onClick={() => {
                    setPricingModalTab('services');
                    setShowPricingModal(true);
                  }}
                  className="px-8 py-3 bg-dark-800 hover:bg-dark-700 text-white font-semibold rounded-lg border border-dark-700 transition-colors"
                >
                  Check out our Services
                </button>
            </div>
          </div>
        </div>
      </section>

      <VersionFooter 
        showFairUseModal={showFairUseModal} 
        setShowFairUseModal={setShowFairUseModal} 
      />

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        initialTab={pricingModalTab}
        user={user}
      />
      {/* Subscription Plans Modal - Stripe */}
      {showSubscriptionPlans && (
        <SubscriptionPlans
          currentUser={user}
          onClose={() => setShowSubscriptionPlans(false)}
        />
      )}

      {/* Billing Management Modal - Stripe */}
      {showBillingManagement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Manage Subscription</h2>
                <button
                  onClick={() => setShowBillingManagement(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <BillingManagement
                currentUser={user}
                onRefresh={() => {
                  setShowBillingManagement(false);
                  setShowSubscriptionPlans(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        prefilledEmail=""
      />

      {/* Guide Modal */}
      <GuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
        defaultTab={guideModalTab}
      />   
      {/* Fair Use Policy Modal */}
      <FairUsePolicyModal
        isOpen={showFairUseModal}
        onClose={() => setShowFairUseModal(false)}
      />   
    </div>
    </>
  );
};

export default LandingPage;
